import { useEffect, useMemo, useState } from "react";
import type { EntityField } from "../../types";

export type ConfigModalType =
  | "text"
  | "textarea"
  | "number"
  | "email"
  | "date"
  | "date_range";

const fieldTypeMap: Record<string, string> = {
  text: "TEXT",
  number: "NUMBER",
  date: "DATE",
  date_range: "DATE",
  email: "EMAIL",
  phone: "PHONE",
  url: "URL",
  textarea: "TEXTAREA",
  select: "SELECT",
  multiselect: "MULTISELECT",
  identification: "TEXT",
  file: "TEXT",
  interoperability: "TEXT",
  boolean: "BOOLEAN",
};

const generateId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `field_${crypto.randomUUID()}`;
  }
  return `field_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

export const useConfigModal = (
  type: ConfigModalType,
  formData?: EntityField,
) => {
  const [fieldName, setFieldNameState] = useState("");
  const [fieldNameError, setFieldNameError] = useState<string | undefined>();
  const [fieldKey, setFieldKey] = useState("");
  const [required, setRequired] = useState(false);
  const [hasDropdown, setHasDropdown] = useState(false);
  const [placeholder, setPlaceholder] = useState("");
  const [maxLength, setMaxLength] = useState("255");
  const [localListItems, setLocalListItems] = useState<string[]>([]);
  const [expandSelectedRecord, setExpandSelectedRecord] =
    useState<boolean>(false);
  const [defaultValueType, setDefaultValueType] = useState<"inp" | "var">(
    "inp",
  );
  const [defaultValue, setDefaultValue] = useState("");
  const [decimals, setDecimals] = useState<"auto" | "1" | "2" | "3">("auto");

  const [buttonPosition, setButtonPosition] = useState<"left" | "right">(
    "left",
  );
  const [valuesSource, setValuesSource] = useState<"database" | "manual">(
    "database",
  );
  // Store table and column names instead of full objects
  const [valuesTableName, setValuesTableName] = useState<string>("");
  const [valuesColumnName, setValuesColumnName] = useState<string>("");

  // email
  const [allowedDomains, setAllowedDomains] = useState<string[]>([]);
  const [noAllowedDomains, setNoAllowedDomains] = useState<string[]>([]);

  // date and datetime
  const [minDate, setMinDate] = useState("");
  const [maxDate, setMaxDate] = useState("");
  const [includeTime, setIncludeTime] = useState(false);
  const [format24, setFormat24] = useState(false);
  const [defaultDate, setDefaultDate] = useState("");
  const [defaultTime, setDefaultTime] = useState("");

  // date and datetime range
  const [defaultStartDate, setDefaultStartDate] = useState("");
  const [defaultStartTime, setDefaultStartTime] = useState("");
  const [defaultEndDate, setDefaultEndDate] = useState("");
  const [defaultEndTime, setDefaultEndTime] = useState("");

  // Wrapper para setFieldName que valida en tiempo real
  const setFieldName = (value: string) => {
    setFieldNameState(value);

    // Validar en tiempo real si el nombre es "ID"
    if (value.trim().toUpperCase() === "ID") {
      setFieldNameError("No se puede crear un campo con el nombre 'ID'");
    } else {
      setFieldNameError(undefined);
    }
  };

  useEffect(() => {
    if (formData) {
      setFieldNameState(formData.name || "");
      setFieldKey(formData.key);
      setRequired(formData.required ?? true);
      setHasDropdown(formData.buttonEnabled || false);
      setPlaceholder(formData.placeholder || "");
      setMaxLength(formData.maxLength?.toString() || "255");
      setDefaultValue(formData.defaultValue || "");
      setButtonPosition(formData.buttonPosition || "left");
      setExpandSelectedRecord(formData.expandSelectedRecord || false);
      setDecimals(formData.decimals || "auto");

      // Email fields
      setAllowedDomains(formData.allowedDomains || []);
      setNoAllowedDomains(formData.noAllowedDomains || []);

      // Date/DateTime fields
      setMinDate(formData.minDate || "");
      setMaxDate(formData.maxDate || "");
      setIncludeTime(formData.includeTime || false);
      setFormat24(formData.format24 || false);
      setDefaultDate(formData.defaultDate || "");
      setDefaultTime(formData.defaultTime || "");

      // Date range fields
      setDefaultStartDate(formData.defaultStartDate || "");
      setDefaultStartTime(formData.defaultStartTime || "");
      setDefaultEndDate(formData.defaultEndDate || "");
      setDefaultEndTime(formData.defaultEndTime || "");

      if (formData.buttonEnabled) {
        setHasDropdown(true);

        if (formData.options_source) {
          setValuesSource(formData.options_source);
        }

        if (formData.options_source === "manual" && formData.options_dropdown) {
          setLocalListItems(formData.options_dropdown);
        } else if (
          formData.options_source === "database" &&
          formData.options_table &&
          formData.options_column
        ) {
          // Just store the names - they'll be looked up via useMemo
          setValuesTableName(formData.options_table);
          setValuesColumnName(formData.options_column);
        }
      }
    }
  }, [formData]);

  const isValidByField: boolean = useMemo(() => {
    if (type === "text" || type === "textarea") {
      return placeholder.trim().length > 0 && maxLength.trim().length > 0;
    } else if (type === "number") {
      return placeholder.trim().length > 0 && decimals !== null;
    } else if (type === "date" || type === "date_range") {
      return format24 !== null && includeTime !== null;
    } else if (type === "email") {
      return (
        placeholder.trim().length > 0 &&
        allowedDomains !== null &&
        noAllowedDomains !== null &&
        allowedDomains.length >= 0 &&
        noAllowedDomains.length >= 0
      );
    }
    return true;
  }, [
    type,
    placeholder,
    maxLength,
    decimals,
    format24,
    includeTime,
    allowedDomains,
    noAllowedDomains,
  ]);

  const isValid: boolean = useMemo(
    () =>
      fieldName.trim().length > 0 &&
      !fieldNameError &&
      fieldKey.trim().length > 0 &&
      required !== null &&
      (!hasDropdown ||
        (hasDropdown &&
          buttonPosition !== null &&
          expandSelectedRecord !== null &&
          (valuesSource === "database"
            ? !!valuesTableName && !!valuesColumnName
            : localListItems.length > 0))) &&
      isValidByField,
    [
      isValidByField,
      fieldName,
      fieldNameError,
      required,
      hasDropdown,
      fieldKey,
      buttonPosition,
      localListItems,
      expandSelectedRecord,
      valuesSource,
      valuesTableName,
      valuesColumnName,
    ],
  );

  const setDataCenterValue = (tableName: string, columnName: string) => {
    // Simply store the names - they'll be looked up automatically via useMemo
    setValuesTableName(tableName);
    setValuesColumnName(columnName);
  };

  const getConfigData = (): EntityField => {
    let saveType = fieldTypeMap[type];

    // Si es date y se incluye tiempo, el tipo de BD es DATETIME
    if (type === "date" && includeTime) {
      saveType = "DATETIME";
    }

    return {
      type,
      fieldType: saveType,
      id: formData?.id || generateId(),
      key: fieldKey,
      name: fieldName,
      required,
      hasDropdown,
      placeholder,
      maxLength,
      defaultValue,
      ...(type === "number" && {
        decimals,
      }),
      ...(type === "email" && {
        allowedDomains,
        noAllowedDomains,
      }),
      ...(type === "date" && {
        minDate,
        maxDate,
        isRange: false,
        includeTime,
        format24,
        defaultDate,
        defaultTime,
      }),
      ...(type === "date_range" && {
        minDate,
        maxDate,
        isRange: true,
        includeTime,
        format24,
        defaultStartDate,
        defaultStartTime,
        defaultEndDate,
        defaultEndTime,
      }),
      buttonEnabled: hasDropdown,
      ...(hasDropdown && {
        buttonPosition,
        expandSelectedRecord,
        options_source: valuesSource,
        ...(valuesSource === "manual" && {
          options_dropdown: localListItems,
        }),
        ...(valuesSource === "database" && {
          options_table: valuesTableName,
          options_column: valuesColumnName,
        }),
      }),
    };
  };

  return {
    fieldName,
    setFieldName,
    fieldNameError,
    fieldKey,
    setFieldKey,
    required,
    setRequired,
    hasDropdown,
    setHasDropdown,
    placeholder,
    setPlaceholder,
    maxLength,
    setMaxLength,
    defaultValue,
    setDefaultValue,
    buttonPosition,
    setButtonPosition,
    valuesSource,
    setValuesSource,
    localListItems,
    setLocalListItems,
    expandSelectedRecord,
    setExpandSelectedRecord,
    defaultValueType,
    setDefaultValueType,
    decimals,
    setDecimals,
    allowedDomains,
    setAllowedDomains,
    noAllowedDomains,
    setNoAllowedDomains,
    minDate,
    setMinDate,
    maxDate,
    setMaxDate,
    includeTime,
    setIncludeTime,
    format24,
    setFormat24,
    defaultDate,
    setDefaultDate,
    defaultTime,
    setDefaultTime,
    defaultStartDate,
    setDefaultStartDate,
    defaultStartTime,
    setDefaultStartTime,
    defaultEndDate,
    setDefaultEndDate,
    defaultEndTime,
    setDefaultEndTime,
    getConfigData,
    valuesTableName,
    valuesColumnName,
    setDataCenterValue,
    isValid,
    isNew: formData?.id ? formData?.id?.includes("field_") : true,
  };
};
